import React, { useCallback, useEffect, useState } from 'react'

import clsx from 'clsx'

import { useAppContext } from 'context/AppContext'
import { useEventContext } from 'context/EventContext'
import createTagScript from 'helpers/createTagScript'

import classes from './YoutubeVideo.module.scss'

const YOUTUBE_IFRAME = 'yt-iframe'
const YOUTUBE_PLAYER = 'yt-player'

/** @type {import("./types").default} */
const YoutubeVideo = ({ video, onPlay, loading, caption, ...otherProps }) => {
  const youtubePlayerId = `${YOUTUBE_PLAYER}-${video.videoId}`
  const hasCaption = !!caption
  const { isCypressTakingSnapshots } = useAppContext()

  const [showYoutubeIframe, setShowYoutubeIframe] = useState(!hasCaption)

  const eventContext = useEventContext()

  const onClickCaption = () => setShowYoutubeIframe(true)

  const loadVideo = useCallback(() => {
    const playerVars = {
      cc_load_policy: 1,
      cc_lang_pref: 'en',
      controls: 1,
      modestbranding: 1,
      rel: 0,
      autoplay: hasCaption ? 1 : 0,
    }

    return new window.YT.Player(youtubePlayerId, {
      playerVars,
      videoId: video.videoId,
      events: {
        onReady: e => {
          // On mobile we need to manually play the video
          if (playerVars.autoplay) {
            e.target.playVideo()
          }
        },
        onStateChange: ({ data = 0 } = {}) => {
          if (data === window.YT.PlayerState.PLAYING) {
            eventContext.sendEvent({
              engagementDestination: 'play',
              engagementDetails: 'video',
              name: 'video_start',
            })
            if (onPlay) {
              onPlay()
            }
          } else if (data === window.YT.PlayerState.PAUSED) {
            eventContext.sendEvent({
              engagementDestination: 'stop',
              engagementDetails: 'video',
              name: 'video_stop',
            })
          }
        },
      },
    })
  }, [video, youtubePlayerId, eventContext, onPlay, hasCaption])

  useEffect(() => {
    if (!showYoutubeIframe || isCypressTakingSnapshots) {
      return
    }

    if (!window.isYouTubeIframeAPIReady) {
      if (!window.onYouTubeIframeAPIReady) {
        createTagScript(YOUTUBE_IFRAME, 'https://www.youtube.com/iframe_api')
        window.YouTubeIframeQueue = []
        // This fn is called from YouTube API when is ready
        window.onYouTubeIframeAPIReady = () => {
          window.YouTubeIframeQueue.forEach(fn => fn())
          window.isYouTubeIframeAPIReady = true
          window.YouTubeIframeQueue = []
        }
      }
      window.YouTubeIframeQueue.push(loadVideo)
    } else {
      loadVideo()
    }
  }, [loadVideo, video, showYoutubeIframe, isCypressTakingSnapshots])

  return showYoutubeIframe ? (
    <div className={clsx('YoutubeVideo', classes.root)}>
      <div
        id={youtubePlayerId}
        className={classes.iframe}
        data-testid={`videoEmbeded-${video.videoId}`}
        loading={loading}
        {...otherProps}
      />
    </div>
  ) : (
    <button className={classes.captionButton} type="button" onClick={onClickCaption}>
      {caption}
    </button>
  )
}

export default YoutubeVideo
